import React, { useEffect, useState, useCallback } from 'react';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Fade from '@mui/material/Fade';
import CircularProgress from '@mui/material/CircularProgress';
import { RxCross2 } from "react-icons/rx";
import { BsArrowRightCircleFill, BsArrowLeftCircleFill } from "react-icons/bs";
import { MdZoomIn, MdZoomOut, MdFullscreen, MdFullscreenExit } from "react-icons/md";

const ImageModal = ({
    isOpen,
    onClose,
    imageUrl,
    sharedMedia,
    handleImageClick
}) => {
    const [currentStartIndex, setCurrentStartIndex] = useState(0);
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [imageError, setImageError] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [zoomLevel, setZoomLevel] = useState(1);
    const imagesPerPage = 5;

    // Sync the selected image with the imageUrl passed as props
    useEffect(() => {
        const index = sharedMedia.findIndex(
            (media) => `/chat-media/${media.file_url}` === imageUrl
        );
        if (index !== -1) {
            setSelectedImageIndex(index);
            // Adjust the start index to show the selected image in the thumbnail view
            const newStartIndex = Math.floor(index / imagesPerPage) * imagesPerPage;
            setCurrentStartIndex(newStartIndex);
        }
    }, [imageUrl, sharedMedia]);

    // Reset states when modal opens
    useEffect(() => {
        if (isOpen) {
            setIsLoading(true);
            setImageError(false);
            setZoomLevel(1);
            setIsFullscreen(false);
        }
    }, [isOpen, selectedImageIndex]);

    const handleNext = useCallback(() => {
        if (selectedImageIndex < sharedMedia.length - 1) {
            const nextIndex = selectedImageIndex + 1;
            setSelectedImageIndex(nextIndex);
            handleImageClick(`/chat-media/${sharedMedia[nextIndex].file_url}`);

            // If the next index is beyond the current view range, increase the start index
            if (nextIndex >= currentStartIndex + imagesPerPage) {
                setCurrentStartIndex((prevIndex) => prevIndex + imagesPerPage);
            }
        }
    }, [selectedImageIndex, sharedMedia, handleImageClick, currentStartIndex, imagesPerPage]);

    const handlePrevious = useCallback(() => {
        if (selectedImageIndex > 0) {
            const prevIndex = selectedImageIndex - 1;
            setSelectedImageIndex(prevIndex);
            handleImageClick(`/chat-media/${sharedMedia[prevIndex].file_url}`);

            // If the previous index is before the current view range, decrease the start index
            if (prevIndex < currentStartIndex) {
                setCurrentStartIndex((prevIndex) => Math.max(0, prevIndex - imagesPerPage + 1));
            }
        }
    }, [selectedImageIndex, sharedMedia, handleImageClick, currentStartIndex, imagesPerPage]);

    const handleClose = useCallback(() => {
        if (isOpen) {
            setSelectedImageIndex(0);
            setCurrentStartIndex(0);
            setZoomLevel(1);
            setIsFullscreen(false);
            onClose();
        }
    }, [isOpen, onClose]);

    const handleZoomIn = () => setZoomLevel(prev => Math.min(prev + 0.25, 3));
    const handleZoomOut = () => setZoomLevel(prev => Math.max(prev - 0.25, 0.5));
    const toggleFullscreen = () => setIsFullscreen(prev => !prev);

    const handleImageLoad = () => {
        setIsLoading(false);
        setImageError(false);
    };

    const handleImageError = () => {
        setIsLoading(false);
        setImageError(true);
    };

    // Add keyboard event listeners when the modal is open
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (isOpen) {
                if (event.key === 'ArrowRight') {
                    handleNext();
                } else if (event.key === 'ArrowLeft') {
                    handlePrevious();
                } else if (event.key === 'Escape') {
                    handleClose();
                } else if (event.key === '+' || event.key === '=') {
                    handleZoomIn();
                } else if (event.key === '-') {
                    handleZoomOut();
                } else if (event.key === 'f' || event.key === 'F') {
                    toggleFullscreen();
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, handleNext, handlePrevious, handleClose]);

    const handleThumbnailClick = (url, index) => {
        setSelectedImageIndex(index);
        handleImageClick(url);
        setIsLoading(true);
        setImageError(false);
    };

    const currentImage = sharedMedia[selectedImageIndex];

    return (
        <Modal
            open={isOpen}
            onClose={handleClose}
            aria-labelledby="image-viewer"
            aria-describedby="modal-to-view-image"
            sx={{
                zIndex: 100000,
                backdropFilter: 'blur(8px)',
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
            }}
            closeAfterTransition
        >
            <Fade in={isOpen} timeout={300}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: isFullscreen ? '95vw' : { xs: '90%', sm: '80%', md: '70%', lg: '60%' },
                        maxWidth: isFullscreen ? 'none' : '1200px',
                        height: isFullscreen ? '95vh' : 'auto',
                        maxHeight: isFullscreen ? 'none' : '90vh',
                        background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
                        borderRadius: isFullscreen ? '0px' : '16px',
                        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        p: 3,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 2,
                        outline: 'none',
                        overflow: 'hidden',
                    }}
                >
                    {/* Header with controls */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            background: 'linear-gradient(135deg, rgba(39, 72, 93, 0.9) 0%, rgba(59, 157, 157, 0.9) 100%)',
                            backdropFilter: 'blur(10px)',
                            p: 2,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            zIndex: 10,
                            borderRadius: isFullscreen ? '0px' : '16px 16px 0 0',
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{
                                color: 'white',
                                fontWeight: 600,
                                fontSize: { xs: '0.9rem', sm: '1.1rem' }
                            }}
                        >
                            {selectedImageIndex + 1} of {sharedMedia.length}
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 1 }}>
                            {/* Zoom controls */}
                            <IconButton
                                onClick={handleZoomOut}
                                disabled={zoomLevel <= 0.5}
                                sx={{
                                    color: 'white',
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.2)' },
                                    '&:disabled': { color: 'rgba(255, 255, 255, 0.3)' }
                                }}
                            >
                                <MdZoomOut />
                            </IconButton>
                            
                            <Typography
                                sx={{
                                    color: 'white',
                                    minWidth: '60px',
                                    textAlign: 'center',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '0.9rem'
                                }}
                            >
                                {Math.round(zoomLevel * 100)}%
                            </Typography>
                            
                            <IconButton
                                onClick={handleZoomIn}
                                disabled={zoomLevel >= 3}
                                sx={{
                                    color: 'white',
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.2)' },
                                    '&:disabled': { color: 'rgba(255, 255, 255, 0.3)' }
                                }}
                            >
                                <MdZoomIn />
                            </IconButton>

                            {/* Fullscreen toggle */}
                            <IconButton
                                onClick={toggleFullscreen}
                                sx={{
                                    color: 'white',
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.2)' }
                                }}
                            >
                                {isFullscreen ? <MdFullscreenExit /> : <MdFullscreen />}
                            </IconButton>

                            {/* Close button */}
                            <IconButton
                                onClick={handleClose}
                                sx={{
                                    color: 'white',
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                    '&:hover': { backgroundColor: 'rgba(220, 53, 69, 0.8)' }
                                }}
                            >
                                <RxCross2 />
                            </IconButton>
                        </Box>
                    </Box>

                    {/* Main Image Container */}
                    <Box
                        sx={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '100%',
                            position: 'relative',
                            mt: 7,
                            mb: isFullscreen ? 2 : 8,
                            overflow: 'hidden',
                            borderRadius: '12px',
                            background: 'rgba(0, 0, 0, 0.3)',
                        }}
                    >
                        {isLoading && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    zIndex: 2,
                                }}
                            >
                                <CircularProgress sx={{ color: '#3B9D9D' }} />
                            </Box>
                        )}

                        {imageError ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    color: 'white',
                                    gap: 2
                                }}
                            >
                                <Typography variant="h6">Failed to load image</Typography>
                                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                                    The image could not be displayed
                                </Typography>
                            </Box>
                        ) : (
                            currentImage?.file_url && (
                                <img
                                    src={`/chat-media/${currentImage?.file_url}`}
                                    alt="Selected"
                                    onLoad={handleImageLoad}
                                    onError={handleImageError}
                                    style={{
                                        maxWidth: '100%',
                                        maxHeight: '100%',
                                        objectFit: 'contain',
                                        transform: `scale(${zoomLevel})`,
                                        transition: 'transform 0.2s ease-in-out',
                                        cursor: zoomLevel > 1 ? 'grab' : 'default',
                                        borderRadius: '8px',
                                    }}
                                />
                            )
                        )}

                        {/* Navigation Arrows */}
                        {sharedMedia.length > 1 && (
                            <>
                                <IconButton
                                    onClick={handlePrevious}
                                    disabled={selectedImageIndex === 0}
                                    sx={{
                                        position: 'absolute',
                                        left: 16,
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        backgroundColor: 'rgba(39, 72, 93, 0.8)',
                                        color: 'white',
                                        width: 56,
                                        height: 56,
                                        '&:hover': {
                                            backgroundColor: 'rgba(39, 72, 93, 0.9)',
                                            transform: 'translateY(-50%) scale(1.1)',
                                        },
                                        '&:disabled': {
                                            backgroundColor: 'rgba(39, 72, 93, 0.3)',
                                            color: 'rgba(255, 255, 255, 0.3)',
                                        },
                                        transition: 'all 0.2s ease-in-out',
                                        fontSize: '1.5rem',
                                    }}
                                >
                                    <BsArrowLeftCircleFill />
                                </IconButton>

                                <IconButton
                                    onClick={handleNext}
                                    disabled={selectedImageIndex === sharedMedia.length - 1}
                                    sx={{
                                        position: 'absolute',
                                        right: 16,
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        backgroundColor: 'rgba(39, 72, 93, 0.8)',
                                        color: 'white',
                                        width: 56,
                                        height: 56,
                                        '&:hover': {
                                            backgroundColor: 'rgba(39, 72, 93, 0.9)',
                                            transform: 'translateY(-50%) scale(1.1)',
                                        },
                                        '&:disabled': {
                                            backgroundColor: 'rgba(39, 72, 93, 0.3)',
                                            color: 'rgba(255, 255, 255, 0.3)',
                                        },
                                        transition: 'all 0.2s ease-in-out',
                                        fontSize: '1.5rem',
                                    }}
                                >
                                    <BsArrowRightCircleFill />
                                </IconButton>
                            </>
                        )}
                    </Box>

                    {/* Thumbnail Images */}
                    {!isFullscreen && sharedMedia.length > 1 && (
                        <Box
                            sx={{
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                background: 'linear-gradient(135deg, rgba(39, 72, 93, 0.95) 0%, rgba(59, 157, 157, 0.95) 100%)',
                                backdropFilter: 'blur(10px)',
                                p: 2,
                                borderRadius: '0 0 16px 16px',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    gap: 2,
                                    overflowX: 'auto',
                                    pb: 1,
                                    '&::-webkit-scrollbar': {
                                        height: '4px',
                                    },
                                    '&::-webkit-scrollbar-track': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                        borderRadius: '2px',
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.3)',
                                        borderRadius: '2px',
                                    },
                                }}
                            >
                                {sharedMedia
                                    ?.slice(currentStartIndex, currentStartIndex + imagesPerPage)
                                    ?.map((media, index) => {
                                        const actualIndex = currentStartIndex + index;
                                        const isSelected = actualIndex === selectedImageIndex;
                                        
                                        return (
                                            <Box
                                                key={actualIndex}
                                                onClick={() => handleThumbnailClick(
                                                    `/chat-media/${media.file_url}`,
                                                    actualIndex
                                                )}
                                                sx={{
                                                    position: 'relative',
                                                    cursor: 'pointer',
                                                    borderRadius: '8px',
                                                    overflow: 'hidden',
                                                    border: isSelected 
                                                        ? '3px solid #3B9D9D' 
                                                        : '2px solid rgba(255, 255, 255, 0.2)',
                                                    transform: isSelected ? 'scale(1.1)' : 'scale(1)',
                                                    transition: 'all 0.3s ease',
                                                    '&:hover': {
                                                        transform: isSelected ? 'scale(1.1)' : 'scale(1.05)',
                                                        border: '3px solid #3B9D9D',
                                                    },
                                                }}
                                            >
                                                <img
                                                    src={`/chat-media/${media.file_url}`}
                                                    alt={`Thumbnail ${actualIndex + 1}`}
                                                    style={{
                                                        width: '60px',
                                                        height: '60px',
                                                        objectFit: 'cover',
                                                        display: 'block',
                                                    }}
                                                />
                                                {isSelected && (
                                                    <Box
                                                        sx={{
                                                            position: 'absolute',
                                                            inset: 0,
                                                            backgroundColor: 'rgba(59, 157, 157, 0.2)',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                width: 8,
                                                                height: 8,
                                                                borderRadius: '50%',
                                                                backgroundColor: '#3B9D9D',
                                                            }}
                                                        />
                                                    </Box>
                                                )}
                                            </Box>
                                        );
                                    })}
                            </Box>
                        </Box>
                    )}

                    {/* Keyboard shortcuts hint */}
                    {!isFullscreen && (
                        <Typography
                            sx={{
                                position: 'absolute',
                                bottom: 8,
                                right: 16,
                                color: 'rgba(255, 255, 255, 0.6)',
                                fontSize: '0.75rem',
                                background: 'rgba(0, 0, 0, 0.5)',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                display: { xs: 'none', sm: 'block' }
                            }}
                        >
                            Use ← → arrows, +/- zoom, F fullscreen, ESC close
                        </Typography>
                    )}
                </Box>
            </Fade>
        </Modal>
    );
};

export default ImageModal;
